import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { trackPageView } from "@/utils/analytics";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { PageTransition } from "@/components/PageTransition";
import { ChatWidget } from "@/components/ChatWidget";
import { BackToTop } from "@/components/BackToTop";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { Layout } from "@/components/layout/Layout";
import { OwnerLayout } from "@/components/layout/OwnerLayout";
import { SkipToContent } from "@/components/SkipToContent";


import Home from "./pages/Home";
import Samboards from "./pages/Samboards";
import LibrarySquares from "./pages/LibrarySquares";
import SensoryPads from "./pages/SensoryPads";
import SensoryAids from "./pages/SensoryAids";
import FidgetSquares from "./pages/FidgetSquares";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Reviews from "./pages/Reviews";
import Professionals from "./pages/Professionals";
import HowProductsHelp from "./pages/HowProductsHelp";
import Satpin from "./pages/Satpin";
import EducationMarketplace from "./pages/EducationMarketplace";
import CareHomes from "./pages/solutions/CareHomes";
import SENDSchools from "./pages/solutions/SENDSchools";
import Charities from "./pages/solutions/Charities";
import HospitalsHealthcare from "./pages/solutions/HospitalsHealthcare";
import LocalAuthorities from "./pages/solutions/LocalAuthorities";
import Corporate from "./pages/solutions/Corporate";
import ParentsCarers from "./pages/solutions/ParentsCarers";
import AnxietyStress from "./pages/mental-health/AnxietyStress";
import Autism from "./pages/mental-health/Autism";
import Dementia from "./pages/mental-health/Dementia";
import Strokes from "./pages/mental-health/Strokes";
import Feedback from "./pages/Feedback";
import DementiaAging from "./pages/feedback/DementiaAging";
import NHS from "./pages/feedback/NHS";
import Headway from "./pages/feedback/Headway";
import SENDCO from "./pages/feedback/SENDCO";
import ResearchStudies from "./pages/ResearchStudies";
import Blog from "./pages/Blog";
import AccessToWorkKits from "./pages/AccessToWorkKits";
import Shop from "./pages/Shop";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/checkout/Success";
import OrderTracking from "./pages/OrderTracking";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import OwnerDashboard from "./pages/OwnerDashboard";
import Login from "./pages/Login";
import Analytics from "./pages/Analytics";

const queryClient = new QueryClient();

// Owner routes that use the OwnerLayout (no public navigation/footer)
const ownerRoutes = ['/login', '/owner-dashboard', '/analytics', '/products', '/customers', '/settings'];

function PublicContent() {
  return (
    <div className="min-h-screen flex flex-col">
      <SkipToContent />
      <Navigation />

      <PageTransition>
        <Layout>
          <main id="main-content" tabIndex={-1}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/samboards" element={<Samboards />} />
              <Route path="/library-squares" element={<LibrarySquares />} />
              <Route path="/sensory-pads" element={<SensoryPads />} />
              <Route path="/sensory-aids" element={<SensoryAids />} />
              <Route path="/fidget-squares" element={<FidgetSquares />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/professionals" element={<Professionals />} />
              <Route path="/how-products-help" element={<HowProductsHelp />} />
              <Route path="/satpin" element={<Satpin />} />
              <Route path="/education-marketplace" element={<EducationMarketplace />} />
              <Route path="/solutions/care-homes" element={<CareHomes />} />
              <Route path="/solutions/send-schools" element={<SENDSchools />} />
              <Route path="/solutions/charities" element={<Charities />} />
              <Route path="/solutions/hospitals-healthcare" element={<HospitalsHealthcare />} />
              <Route path="/solutions/local-authorities" element={<LocalAuthorities />} />
              <Route path="/solutions/corporate" element={<Corporate />} />
              <Route path="/solutions/parents-carers" element={<ParentsCarers />} />
              <Route path="/mental-health/anxiety-stress" element={<AnxietyStress />} />
              <Route path="/mental-health/autism" element={<Autism />} />
              <Route path="/mental-health/dementia" element={<Dementia />} />
              <Route path="/mental-health/strokes" element={<Strokes />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/feedback/dementia-aging" element={<DementiaAging />} />
              <Route path="/feedback/nhs" element={<NHS />} />
              <Route path="/feedback/headway" element={<Headway />} />
              <Route path="/feedback/sendco" element={<SENDCO />} />
              <Route path="/research-studies" element={<ResearchStudies />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/access-to-work-kits" element={<AccessToWorkKits />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/order-tracking" element={<OrderTracking />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </Layout>
      </PageTransition>

      <Footer />
      <AccessibilityWidget />
      <ChatWidget />
      <BackToTop />
    </div>
  );
}

function OwnerContent() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/owner-dashboard"
        element={
          <ProtectedRoute>
            <OwnerLayout>
              <OwnerDashboard />
            </OwnerLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <OwnerLayout>
              <Analytics />
            </OwnerLayout>
          </ProtectedRoute>
        }
      />
      {/* Convenience routes that redirect to dashboard with tab param */}
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Navigate to="/owner-dashboard?tab=products" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <Navigate to="/owner-dashboard?tab=customers" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Navigate to="/owner-dashboard?tab=settings" replace />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function AppContent() {
  const location = useLocation();

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Check if current route is an owner route
  const isOwnerRoute = ownerRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Render appropriate layout based on route
  if (isOwnerRoute) {
    return <OwnerContent />;
  }

  return <PublicContent />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <CartProvider>
      <TooltipProvider>
        {/* Global toasts */}
        <Toaster />
        <Sonner />

        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </CartProvider>
  </QueryClientProvider>
);

export default App;